import {
	ConfigurationConfig,
	CreatorRollerBox,
	CreatorSegment,
	CreatorState,
	CreatorWindow,
	CreatorWing,
	SegmentDivisionType,
	WingDivisionType,
} from '@/types/creator';
import { WritableDraft } from '@reduxjs/toolkit';
import { clamp } from 'lodash';
import { MontageType, RollerShaftDiameter } from '@/api/ProductTypeApi';
import { UUID } from '@/types/types';
import { setSegmentsArmorErrors } from '@/utils/creatorUtils';
import APP_CONFIG from '@/configs/appConfig';

class DimensionsHandler {
	constructor() {}

	computeSegmentsQuantity(state: WritableDraft<CreatorState>) {
		const width = clamp(
			state.projectDimensions.width,
			state.configurationConfig.project.minWidth,
			state.configurationConfig.project.maxWidth
		);

		const height = clamp(
			state.projectDimensions.height,
			state.configurationConfig.project.minHeight,
			state.configurationConfig.project.maxHeight
		);

		const currentSegmentsQuantity = state.segments.length;

		const { min: minSegments, max: maxSegments } = this.calculateMinMaxSegments(
			width,
			state.configurationConfig
		);

		const desiredSegmentsQuantity = clamp(
			currentSegmentsQuantity,
			minSegments,
			maxSegments
		);

		const calculatedWidth = Math.floor(width / desiredSegmentsQuantity);
		let remainder = width % desiredSegmentsQuantity;

		state.segments = [];

		const windowHeight = height - state.projectDimensions.rollerBoxHeight;

		for (let i = 0; i < desiredSegmentsQuantity; i++) {
			const newSegment = this.getNewSegment(windowHeight, calculatedWidth);

			state.segments.push(newSegment);
		}

		this.computeDivisions(state);

		for (const segment of state.segments) segment.width = calculatedWidth;

		let idx = 0;
		while (remainder > 0) {
			state.segments[idx].width += 1;
			remainder--;
			idx++;
		}

		for (const segment of state.segments)
			this.setSegmentWingsEvenWidth(segment);

		state.projectDimensions.segmentsQuantity = state.segments.length;
		this.setSegmentsBelowMinRecommendedWidth(state);
		setSegmentsArmorErrors(state);
	}

	setMaxProjectHeight(state: WritableDraft<CreatorState>) {
		const { selectedValues, configurationConfig } = state;
		const selectedAttributeIds = Object.values(selectedValues)
				.filter(Boolean)
				.map((val) => val!.id),
			userWithMosquitoNet = configurationConfig.project.allowMosquitoNet;

		let maxHeight: number =
				APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MIN.HEIGHT,
			matchFound = false;

		if (!state.productType) {
			state.configurationConfig.project.maxHeight =
				APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MAX.HEIGHT;
			return;
		}

		for (const config of state.productType.rollerBoxHeightConfigs) {
			if (
				!this.matchesAdditionalAttributeValues(
					config.additionalAttributeValues,
					selectedAttributeIds
				)
			)
				continue;

			let attributeValueId: UUID;
			for (attributeValueId in config.attributeValuesConfig) {
				if (config.withMosquitoNet !== userWithMosquitoNet) continue;
				if (!selectedAttributeIds.includes(attributeValueId)) continue;
				const shaftConfigs = config.attributeValuesConfig[attributeValueId];

				let shaftDiameter: any;
				for (shaftDiameter in shaftConfigs) {
					const shaftDiameterConfig =
						shaftConfigs[shaftDiameter as RollerShaftDiameter];

					if (shaftDiameterConfig.maxHr > maxHeight) {
						maxHeight = shaftDiameterConfig.maxHr;
						matchFound = true;
					}
				}
			}
		}

		state.configurationConfig.project.maxHeight = matchFound
			? maxHeight
			: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MAX.HEIGHT;
	}

	setSegmentsQuantity(state: WritableDraft<CreatorState>, quantity: number) {
		state.segments = [];

		const base = Math.floor(state.projectDimensions.width / quantity);
		let remainder = state.projectDimensions.width - base * quantity;

		const windowHeight =
			state.projectDimensions.height - state.projectDimensions.rollerBoxHeight;

		for (let i = 0; i < quantity; i++) {
			const width = base + (remainder > 0 ? 1 : 0);
			if (remainder > 0) remainder--;

			const newSegment = this.getNewSegment(windowHeight, width);

			state.segments.push(newSegment);
		}

		this.computeDivisions(state);
		state.projectDimensions.segmentsQuantity = state.segments.length;
		this.setSegmentsBelowMinRecommendedWidth(state);
		setSegmentsArmorErrors(state);
	}

	calculateMinMaxSegments(
		configurationWidth: number,
		configurationConfig: ConfigurationConfig
	) {
		const min = Math.ceil(
			configurationWidth / configurationConfig.segment.maxWidth
		);
		const max = Math.floor(
			configurationWidth / configurationConfig.segment.minWidth
		);

		return { min, max };
	}

	getNewSegment(windowHeight: number, calculatedWidth: number) {
		const rollerBox: CreatorRollerBox = {
			id: crypto.randomUUID(),
		};

		const wing: CreatorWing = {
			id: crypto.randomUUID(),
			isMosquitoError: false,
			width: calculatedWidth,
			withMosquitoNet: false,
		};

		const window: CreatorWindow = {
			id: crypto.randomUUID(),
			height: windowHeight,
			wings: [wing],
			wingDivisions: [],
		};

		const newSegment: CreatorSegment = {
			id: crypto.randomUUID(),
			isArmorError: false,
			isBelowMinRecommendedWidth: false,
			width: calculatedWidth,
			rollerBox,
			window,
			nonPerforatedSlatCount: 0,
		};

		return newSegment;
	}

	handleSegmentWidthChange(
		active: CreatorSegment,
		state: WritableDraft<CreatorState>
	) {
		const projectWidth = state.projectDimensions.width,
			segments = state.segments,
			lastSegment = segments[segments.length - 1],
			{ minWidth, maxWidth } = state.configurationConfig.segment;

		active.width = clamp(active.width, minWidth, maxWidth);

		let widthSumExceptLast = 0;
		for (const segment of segments) {
			if (segment === lastSegment) continue;
			widthSumExceptLast += segment.width;
		}
		lastSegment.width = projectWidth - widthSumExceptLast;

		if (lastSegment.width < minWidth) {
			const nonLast = segments.slice(0, -1),
				overflow = widthSumExceptLast + minWidth - projectWidth,
				donors: WritableDraft<CreatorSegment>[] = [];

			for (let i = nonLast.length - 1; i >= 0; i--) {
				const s = nonLast[i];
				if (s === active) continue;
				donors.push(s);
			}
			if (active !== lastSegment) donors.push(active);

			this.applyCorrection(donors, overflow, state.configurationConfig);

			widthSumExceptLast = 0;
			for (const segment of segments) {
				if (segment === lastSegment) continue;
				widthSumExceptLast += segment.width;
			}
			lastSegment.width = projectWidth - widthSumExceptLast;
		}

		for (const segment of state.segments) {
			this.setSegmentWingsEvenWidth(segment);
		}

		this.setSegmentsBelowMinRecommendedWidth(state);
	}

	computeWingsDivisions(state: WritableDraft<CreatorState>, segmentId?: UUID) {
		if (segmentId) {
			for (const segment of state.segments) {
				if (segment.id !== segmentId) continue;
				segment.window.wingDivisions = [];
				for (let index = 0; index < segment.window.wings.length - 1; index++) {
					segment.window.wingDivisions.push(WingDivisionType.NONE);
				}
				break;
			}
			return;
		}

		for (const segment of state.segments) {
			segment.window.wingDivisions = [];
			for (let index = 0; index < segment.window.wings.length - 1; index++) {
				segment.window.wingDivisions.push(WingDivisionType.NONE);
			}
		}
	}

	setAvailableRollerBoxHeights(state: WritableDraft<CreatorState>) {
		const availableRollerBoxHeights = this.getAvailableRollerBoxHeights(state);
		state.configurationConfig.rollerBox.availableHeights =
			availableRollerBoxHeights;

		if (
			availableRollerBoxHeights.includes(
				state.projectDimensions.rollerBoxHeight
			)
		)
			return;

		state.projectDimensions.rollerBoxHeight = availableRollerBoxHeights[0];
	}

	setAvailableRollerBoxDepths(state: WritableDraft<CreatorState>) {
		const availableRollerBoxDepths = this.getAvailableRollerBoxDepths(state);

		state.configurationConfig.rollerBox.availableDepths =
			availableRollerBoxDepths;

		if (
			!availableRollerBoxDepths.includes(state.projectDimensions.rollerBoxDepth)
		)
			state.projectDimensions.rollerBoxDepth = availableRollerBoxDepths[0];

		this.setAutoSelectedShaftDiameter(state);
	}

	adjustWindowHeights(state: WritableDraft<CreatorState>) {
		const hr = state.projectDimensions.height,
			rollerBoxHeight = state.projectDimensions.rollerBoxHeight,
			maxWindowHeight = hr - rollerBoxHeight;

		for (const segment of state.segments) {
			segment.window.height = Math.min(segment.window.height, maxWindowHeight);
		}
	}

	setAutoSelectedShaftDiameter(state: WritableDraft<CreatorState>) {
		const {
			productType,
			selectedValues,
			projectDimensions,
			configurationConfig,
		} = state;
		const hr = projectDimensions.height;
		const userWithMosquitoNet = configurationConfig.project.allowMosquitoNet;

		if (!productType) {
			state.projectDimensions.shaftDiameter = 40;
			return;
		}

		const selectedAttributeIds = Object.values(selectedValues)
			.filter(Boolean)
			.map((val) => val!.id as UUID);

		const config = productType.rollerBoxHeightConfigs.find((c) => {
			if (
				!this.matchesAdditionalAttributeValues(
					c.additionalAttributeValues,
					selectedAttributeIds
				)
			)
				return false;

			if (c.rollerBoxHeight !== projectDimensions.rollerBoxHeight) return false;
			if (c.rollerBoxDepth !== projectDimensions.rollerBoxDepth) return false;
			if (c.withMosquitoNet !== userWithMosquitoNet) return false;

			return true;
		});

		if (!config) {
			state.projectDimensions.shaftDiameter = 40;
			return;
		}

		const availableShaftDiameters =
			APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_SHAFT_DIAMETERS;

		const validDiameters = availableShaftDiameters.filter((diameter) => {
			return selectedAttributeIds.every((attrId) => {
				const shaftConfigForAttr = config.attributeValuesConfig[attrId];
				if (!shaftConfigForAttr) return true;

				const limit = shaftConfigForAttr[diameter];
				if (!limit) return false;

				return hr <= limit.maxHr;
			});
		});

		if (validDiameters.length === 0) {
			state.projectDimensions.shaftDiameter = 40;
			return;
		}

		const largestShaft = Math.max(...validDiameters) as RollerShaftDiameter;
		state.projectDimensions.shaftDiameter = largestShaft;
	}

	setSegmentWingsWidth(
		segment: WritableDraft<CreatorSegment>,
		activeWing: WritableDraft<CreatorWing>,
		width: number,
		minWidth: number
	) {
		const wings = segment.window.wings,
			totalWidth = segment.width,
			lastWing = wings[wings.length - 1];

		if (wings.length === 1) {
			activeWing.width = totalWidth;
			return;
		}

		activeWing.width = clamp(width, minWidth, totalWidth);

		let widthSumExceptLast = 0;
		for (const wing of wings) {
			if (wing === lastWing) continue;
			widthSumExceptLast += wing.width;
		}
		lastWing.width = totalWidth - widthSumExceptLast;

		if (lastWing.width >= minWidth) return;

		const nonLast = wings.slice(0, -1);
		let overflow = widthSumExceptLast + minWidth - totalWidth;
		const donors: WritableDraft<CreatorWing>[] = [];

		for (let i = nonLast.length - 1; i >= 0; i--) {
			const wing = nonLast[i];
			if (wing === activeWing) continue;
			donors.push(wing);
		}
		if (activeWing !== lastWing) donors.push(activeWing);

		for (const wing of donors) {
			if (overflow === 0) break;

			const canReduce = wing.width - minWidth;
			if (canReduce <= 0) continue;

			const delta = Math.min(canReduce, overflow);
			wing.width -= delta;
			overflow -= delta;
		}

		widthSumExceptLast = 0;
		for (const wing of wings) {
			if (wing === lastWing) continue;
			widthSumExceptLast += wing.width;
		}
		lastWing.width = totalWidth - widthSumExceptLast;
	}

	adjustWindowHeightsByRollerBoxHeightChange(
		state: WritableDraft<CreatorState>,
		rollerBoxHeightDiff: number
	) {
		if (state.creatorProject.selectedMontageType === MontageType.ON_WALL)
			return;

		for (const segment of state.segments) {
			segment.window.height -= rollerBoxHeightDiff;
		}
	}

	private getAvailableRollerBoxHeights(state: WritableDraft<CreatorState>) {
		const {
			productType,
			selectedValues,
			projectDimensions,
			configurationConfig,
		} = state;
		const hr = projectDimensions.height,
			userWithMosquitoNet = configurationConfig.project.allowMosquitoNet;

		if (!productType)
			return [
				...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_HEIGHTS,
			];

		const selectedAttributeIds = Object.values(selectedValues)
			.filter(Boolean)
			.map((val) => val!.id);

		const availableHeights = productType.rollerBoxHeightConfigs
			.filter((config) => {
				if (
					!this.matchesAdditionalAttributeValues(
						config.additionalAttributeValues,
						selectedAttributeIds
					)
				)
					return false;

				if (config.withMosquitoNet !== userWithMosquitoNet) return false;

				return selectedAttributeIds.some((attrId) => {
					const shaftConfig = config.attributeValuesConfig[attrId as UUID];
					if (!shaftConfig) return false;

					return Object.values(shaftConfig).some((limit) => hr <= limit.maxHr);
				});
			})
			.map((config) => config.rollerBoxHeight);

		if (!availableHeights.length)
			return [
				...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_HEIGHTS,
			];

		return Array.from(new Set(availableHeights)).sort((a, b) => a - b);
	}

	private getAvailableRollerBoxDepths(state: WritableDraft<CreatorState>) {
		const { productType, selectedValues, projectDimensions } = state;
		const hr = projectDimensions.height;

		const selectedHeight = projectDimensions.rollerBoxHeight;

		if (!productType)
			return [...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_DEPTHS];

		const selectedAttributeIds = Object.values(selectedValues)
			.filter(Boolean)
			.map((val) => val!.id);

		const availableDepths = productType.rollerBoxHeightConfigs
			.filter((config) => {
				if (
					!this.matchesAdditionalAttributeValues(
						config.additionalAttributeValues,
						selectedAttributeIds
					)
				)
					return false;
				if (config.rollerBoxHeight !== selectedHeight) return false;

				return selectedAttributeIds.some((attrId) => {
					const shaftConfig = config.attributeValuesConfig[attrId as UUID];
					return (
						shaftConfig &&
						Object.values(shaftConfig).some((limit) => hr <= limit.maxHr)
					);
				});
			})
			.map((config) => config.rollerBoxDepth);

		if (!availableDepths.length)
			return [...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_DEPTHS];

		return Array.from(new Set(availableDepths)).sort((a, b) => a - b);
	}

	private computeDivisions(state: WritableDraft<CreatorState>) {
		state.segmentJoints = [];

		for (let index = 0; index < state.segments.length - 1; index++) {
			state.segmentJoints.push({
				divisionType: SegmentDivisionType.NONE,
				driverJunction: false,
			});
		}

		this.computeWingsDivisions(state);
	}

	private applyCorrection(
		list: WritableDraft<CreatorSegment[]>,
		overflow: number,
		config: WritableDraft<ConfigurationConfig>
	) {
		for (const s of list) {
			if (overflow === 0) break;

			if (overflow > 0) {
				const canReduce = s.width - config.segment.minWidth;
				if (canReduce <= 0) continue;

				const delta = Math.min(canReduce, overflow);
				s.width -= delta;
				overflow -= delta;
			} else {
				const canGrow = config.segment.maxWidth - s.width;
				if (canGrow <= 0) continue;

				const delta = Math.min(canGrow, -overflow);
				s.width += delta;
				overflow += delta;
			}
		}
		return overflow;
	}

	private setSegmentsBelowMinRecommendedWidth(
		state: WritableDraft<CreatorState>
	) {
		const { recommendedMinWidth } = state.configurationConfig.segment;

		for (const segment of state.segments)
			segment.isBelowMinRecommendedWidth = segment.width < recommendedMinWidth;
	}

	private setSegmentWingsEvenWidth(segment: WritableDraft<CreatorSegment>) {
		const wings = segment.window.wings;
		const count = wings.length;

		if (count === 0) return;

		const totalWidth = segment.width;

		const base = Math.floor(totalWidth / count);
		let remainder = totalWidth - base * count;

		for (const wing of wings) {
			wing.width = base;
			if (remainder > 0) {
				wing.width += 1;
				remainder--;
			}
		}
	}

	private matchesAdditionalAttributeValues(
		additionalAttributeValues: UUID[],
		selectedAttributeIds: UUID[]
	) {
		if (!additionalAttributeValues.length) return true;

		return additionalAttributeValues.every((additionalAttributeValue) =>
			selectedAttributeIds.includes(additionalAttributeValue)
		);
	}
}

export default new DimensionsHandler();
